import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { calendarApi } from "@/services/api";
import { useAuth } from "@/hooks/useAuth";
import type { CalendarEvent } from "@/types";
import { format } from "date-fns";
import { de } from "date-fns/locale";
import {
  ArrowLeft,
  Calendar,
  Clock,
  MapPin,
  FileText,
  Tag,
  Loader2,
  AlertCircle,
  Trash2,
  Pencil,
  User,
  CheckCircle
} from "lucide-react";

const eventTypeLabels: Record<string, string> = {
  company_event: "Firmenevent",
  meeting: "Besprechung",
  holiday: "Feiertag",
  birthday: "Geburtstag",
  arzttermin: "Arzttermin",
  urlaub: "Urlaub",
  other: "Sonstiges"
};

export function EventDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { isAdmin: _isAdmin } = useAuth();
  const [event, setEvent] = useState<CalendarEvent | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (id) {
      loadEvent();
    }
  }, [id]);

  const loadEvent = async () => {
    setIsLoading(true);
    try {
      const response = await calendarApi.getById(Number(id));
      setEvent(response.data.data);
    } catch (err: any) {
      setError(err.response?.data?.message || "Termin konnte nicht geladen werden");
    } finally {
      setIsLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!event || !confirm("Möchten Sie diesen Termin wirklich löschen?")) return;
    setIsSubmitting(true);
    try {
      await calendarApi.delete(event.id);
      navigate("/calendar", {
        state: { message: "Löschvorschlag wurde eingereicht." }
      });
    } catch (err: any) {
      setError(err.response?.data?.message || "Fehler beim Löschen");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEdit = () => {
    navigate(`/calendar/${id}/edit`);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-primary-600" />
      </div>
    );
  }

  if (error || !event) {
    return (
      <div className="max-w-2xl mx-auto">
        <div className="mb-4">
          <button
            onClick={() => navigate("/calendar")}
            className="inline-flex items-center text-sm text-gray-600 hover:text-gray-900"
          >
            <ArrowLeft className="h-4 w-4 mr-1" />
            Zurück zum Kalender
          </button>
        </div>
        <div className="p-4 bg-red-50 border border-red-200 rounded-lg flex items-center gap-3">
          <AlertCircle className="h-5 w-5 text-red-500" />
          <p className="text-red-700">{error || "Termin nicht gefunden"}</p>
        </div>
      </div>
    );
  }

  const startDate = new Date(event.start_date.replace(" ", "T"));
  const endDate = event.end_date ? new Date(event.end_date.replace(" ", "T")) : null;

  return (
    <div className="max-w-2xl mx-auto">
      {/* Back button */}
      <div className="mb-4">
        <button
          onClick={() => navigate("/calendar")}
          className="inline-flex items-center text-sm text-gray-600 hover:text-gray-900"
        >
          <ArrowLeft className="h-4 w-4 mr-1" />
          Zurück zum Kalender
        </button>
      </div>

      {/* Header */}
      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        <div className="p-6">
          <div className="flex items-start justify-between">
            <div>
              <div className="flex items-center gap-2 mb-2">
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-primary-100 text-primary-800">
                  <Tag className="h-3 w-3 mr-1" />
                  {eventTypeLabels[event.event_type] || event.event_type}
                </span>
                {event.is_all_day && (
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                    Ganztägig
                  </span>
                )}
              </div>
              <h1 className="text-2xl font-bold text-gray-900">{event.title}</h1>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={handleEdit}
                disabled={isSubmitting}
                className="p-2 text-gray-500 hover:text-primary-600 hover:bg-primary-50 rounded-lg transition-colors"
                title="Bearbeiten"
              >
                <Pencil className="h-5 w-5" />
              </button>
              <button
                onClick={handleDelete}
                disabled={isSubmitting}
                className="p-2 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                title="Löschen"
              >
                <Trash2 className="h-5 w-5" />
              </button>
            </div>
          </div>

          {/* Details */}
          <div className="mt-6 space-y-4">
            {/* Date/Time */}
            <div className="flex items-start gap-3">
              <Calendar className="h-5 w-5 text-gray-400 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-gray-900">
                  {format(startDate, "EEEE, d. MMMM yyyy", { locale: de })}
                </p>
                {!event.is_all_day && (
                  <p className="text-sm text-gray-500">
                    <Clock className="h-4 w-4 inline mr-1" />
                    {format(startDate, "HH:mm", { locale: de })}
                    {endDate && ` - ${format(endDate, "HH:mm", { locale: de })}`}
                  </p>
                )}
              </div>
            </div>

            {/* Location */}
            {event.location && (
              <div className="flex items-start gap-3">
                <MapPin className="h-5 w-5 text-gray-400 mt-0.5" />
                <p className="text-sm text-gray-900">{event.location}</p>
              </div>
            )}

            {/* Description */}
            {event.description && (
              <div className="flex items-start gap-3">
                <FileText className="h-5 w-5 text-gray-400 mt-0.5" />
                <p className="text-sm text-gray-900 whitespace-pre-wrap">{event.description}</p>
              </div>
            )}

            {/* Creator */}
            {event.created_by_name && (
              <div className="flex items-start gap-3">
                <User className="h-5 w-5 text-gray-400 mt-0.5" />
                <p className="text-sm text-gray-900">
                  Erstellt von <span className="font-medium">{event.created_by_name}</span>
                </p>
              </div>
            )}

            {/* Approval info */}
            {event.approved_by_name && (
              <div className="flex items-start gap-3">
                <CheckCircle className="h-5 w-5 text-green-500 mt-0.5" />
                <p className="text-sm text-gray-900">
                  Genehmigt von <span className="font-medium">{event.approved_by_name}</span>
                  {event.approved_at && (
                    <span className="text-gray-500">
                      {" "}am {format(new Date(event.approved_at.replace(" ", "T")), "dd.MM.yyyy", { locale: de })}
                    </span>
                  )}
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
